// @givmo/board — the title block and tab strip every page of one meeting shares (CBG-UX1).
//
// A meeting's four pages — Overview, Packet, Meeting record, Minutes — are ONE place to the officer,
// so they share one header: the meeting's title, its type and time, its actions, and the tabs. Each
// tab is a LINK to that page's own address, never an in-page panel, and that is load-bearing rather
// than a routing detail: the packet, the meeting record and the minutes keep the addresses every
// earlier link and bookmark already points at, and a real link can be opened in a new tab or copied.
// (A director's emailed link lands in the director tree, `/board/director/…`, never on these pages.) The strip is a `nav` whose current page carries `aria-current="page"`; it wears the
// design system's SaaS tab look so it reads as the tabs it is.
//
// The actions are the meeting's own, on every tab: "Edit" and a "⋯" menu holding "Add to calendar"
// and "Cancel meeting" for someone who can change the meeting (`board_meeting:write`, while it is not
// cancelled) — red text in the menu, no solid red button — and "Add to calendar" alone for everyone
// else. "Edit" shows only while the backend would take an edit (`acceptsMeetingEdits`: the meeting's
// start is still ahead); a past meeting keeps the menu, since cancelling one is still accepted.
// "Add to calendar" goes once the meeting has STARTED (CBG-FIX25: its start known and at or before
// now, on the minute clock and at the start itself): nobody is asked whether they can make it then. A CANCELLED meeting is nobody's to attend or to change, so its one action is "Update your
// calendar": the same file, now saying CANCELLED at a higher sequence (the orchestrator's by-eye
// passes). A control a person cannot use is absent, never disabled with a note (Jonathan, 2026-09-28).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, CalendarSync, MoreHorizontal, XCircle } from 'lucide-react';
import { isGivmoError, type OrgBoardMeetingOut } from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  INSET_FOCUS_RING,
  Icon,
  IconButton,
  Menu,
  TAB_LANE,
  TextField,
  cn,
  revealInLane,
  toast,
} from '@givmo/design-system';
import { Link } from '../../board-routing';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { PageShell } from '../../components/page-shell';
import { StatusWord } from '../../components/fine-print';
import { downloadMeetingCalendar } from './calendar-download';
import { MeetingFormDialog } from './meeting-form-dialog';
import {
  acceptsMeetingEdits,
  meetingCommitteeName,
  meetingHasStarted,
  meetingTypeLabel,
  meetingWhen,
} from './meetings-decode';
import { useMinuteClock } from '../../lib/minute-clock';
import { useHoldsInvitation } from '../calendar/calendar-file-rule';

export type MeetingTab = 'overview' | 'packet' | 'record' | 'minutes';

/** Each tab's page, as the package's own relative addresses. */
const TABS: readonly { tab: MeetingTab; label: string; to: string; testId: string }[] = [
  {
    tab: 'overview',
    label: 'Overview',
    to: '/meetings/$meetingId',
    testId: 'meeting-tab-overview',
  },
  {
    tab: 'packet',
    label: 'Packet',
    to: '/meetings/$meetingId/packet',
    testId: 'meeting-packet-link',
  },
  {
    tab: 'record',
    label: 'Meeting record',
    to: '/meetings/$meetingId/record',
    testId: 'meeting-record-link',
  },
  {
    tab: 'minutes',
    label: 'Minutes',
    to: '/meetings/$meetingId/minutes',
    testId: 'meeting-minutes-link',
  },
];

export interface MeetingFrameProps {
  /**
   * The meeting, once read. Until then — or if that read fails — the frame is the page's own name
   * alone, in the SAME element tree, so a page that renders before the meeting arrives (the packet)
   * never has its content remounted, and its state dropped, when the read lands.
   */
  meeting: OrgBoardMeetingOut | null;
  /** The page's own name, shown while there is no meeting to title it. */
  fallbackTitle?: string;
  /** The page being shown — its tab is the current one. */
  tab: MeetingTab;
  /** The page's own root testid (the e2e/RTL data contract each page already had). */
  testId: string;
  children: ReactNode;
}

export function MeetingFrame({
  meeting,
  fallbackTitle = 'Meeting',
  tab,
  testId,
  children,
}: MeetingFrameProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { orgRef } = useBoardHost();
  // Editing and cancelling are `board_meeting:write`, and a cancelled meeting stands as recorded.
  const canWrite = useBoardPermission('board_meeting:write');
  const cancelled = meeting?.status === 'cancelled';
  const canChange = canWrite && !cancelled;
  // Edit only where the backend would take the edit: it refuses every change to a meeting whose start
  // has passed, while cancelling one stays open — so a past meeting keeps the "⋯" menu and no Edit.
  const canEdit = canChange && meeting !== null && acceptsMeetingEdits(meeting);
  const now = useMinuteClock(meeting?.starts_at ?? null);
  const started = meeting !== null && meetingHasStarted(meeting.starts_at, now);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // CBG-CAL1 question 10: no calendar file for someone who already holds the meeting's invitation
  // (it would make a second entry); everyone else keeps it. Unknown while the reads settle.
  const offersFile = useHoldsInvitation(meeting) === false;

  const meetingKey = operations.officerGetBoardMeetingOptions({
    path: { org_ref: orgRef ?? '', meeting_id: meeting?.id ?? '' },
  }).queryKey;
  const refresh = () => queryClient.invalidateQueries({ queryKey: meetingKey });

  if (meeting === null) {
    return (
      <PageShell title={fallbackTitle} testId={testId}>
        {null}
        <TabContent underTabs={false}>{children}</TabContent>
      </PageShell>
    );
  }

  async function onDownloadCalendar() {
    if (orgRef === null || meeting === null) return;
    try {
      await downloadMeetingCalendar(
        { orgRef, meetingId: meeting.id, title: meeting.title },
        operations,
      );
    } catch (caught) {
      toast.error(
        isGivmoError(caught)
          ? caught.message
          : 'We couldn’t build the calendar file. Please try again.',
      );
    }
  }

  const committee = meetingCommitteeName(meeting);
  return (
    <PageShell
      title={meeting.title}
      description={
        <>
          {/* CBG-YR6-MEET (frame c5): a committee's meeting names its committee first. */}
          {committee !== null ? (
            <span className="font-w600 text-text-secondary" data-testid="meeting-committee">
              {committee} ·{' '}
            </span>
          ) : null}
          {meeting.meeting_type ? `${meetingTypeLabel(meeting.meeting_type)} meeting · ` : null}
          {/* The long date where it fits; a phone takes the short one, so the zone never wraps alone. */}
          <span className="hidden sm:inline">{meetingWhen(meeting, 'long')}</span>
          <span className="sm:hidden">{meetingWhen(meeting, 'short')}</span>
          {cancelled ? (
            <>
              {' · '}
              <StatusWord testId="meeting-cancelled">Cancelled</StatusWord>
            </>
          ) : null}
        </>
      }
      actions={
        canChange ? (
          <div className="flex items-center gap-8">
            {canEdit ? (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => setEditing(true)}
                testId="meeting-edit"
              >
                Edit
              </Button>
            ) : null}
            <Menu.Root>
              <Menu.Trigger
                render={
                  <IconButton
                    aria-label="More for this meeting"
                    tone="secondary"
                    size="md"
                    testId="meeting-more"
                  />
                }
              >
                <Icon icon={MoreHorizontal} size={18} aria-hidden />
              </Menu.Trigger>
              <Menu.Content saas align="end" className="min-w-[200px]" testId="meeting-more-menu">
                {!started && offersFile ? (
                  <>
                    <Menu.Item
                      saas
                      startIcon={<Icon icon={CalendarPlus} size={16} aria-hidden />}
                      onClick={() => void onDownloadCalendar()}
                      testId="meeting-download-calendar"
                    >
                      Add to calendar
                    </Menu.Item>
                    <Menu.Separator className="my-4 bg-border-subtle" />
                  </>
                ) : null}
                <Menu.Item
                  saas
                  tone="danger"
                  startIcon={<Icon icon={XCircle} size={16} aria-hidden />}
                  onClick={() => setCancelling(true)}
                  testId="meeting-cancel"
                >
                  Cancel meeting
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        ) : (cancelled || !started) && offersFile ? (
          // Everyone else's one action is the calendar file, a read. For a cancelled meeting it is the
          // same file saying CANCELLED at a higher sequence. The file is a snapshot (it declares no
          // METHOD, RFC 5545 §3.7.2), so whether a calendar drops the entry is the calendar's call:
          // hence "Update", never "Remove" (the orchestrator's by-eye pass 2).
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={
              <Icon icon={cancelled ? CalendarSync : CalendarPlus} size={16} aria-hidden />
            }
            onClick={() => void onDownloadCalendar()}
            testId="meeting-download-calendar"
          >
            {cancelled ? 'Update your calendar' : 'Add to calendar'}
          </Button>
        ) : null
      }
      testId={testId}
    >
      <MeetingTabs meetingId={meeting.id} current={tab} />
      <TabContent underTabs>{children}</TabContent>

      {editing && canEdit && orgRef !== null ? (
        <MeetingFormDialog
          orgRef={orgRef}
          meeting={meeting}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      ) : null}

      {cancelling && orgRef !== null ? (
        <CancelMeetingDialog
          orgRef={orgRef}
          meetingId={meeting.id}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            void refresh();
          }}
        />
      ) : null}
    </PageShell>
  );
}

/**
 * The four pages of one meeting, as links in the design system's SaaS tab look, on the design
 * system's lane (`TAB_LANE`, CBG-DS2). At a 16px gap the four labels need 327px: a 375px phone has
 * that, but a 320px one leaves a 272px column. There the strip scrolls inside itself instead of
 * widening the page, and the current page's tab is brought whole into view. Below 360px the gap is
 * 12px, so the tab past the lane's edge shows the start of its label ("Mi…") rather than falling
 * behind the gap and the lane showing no sign of it.
 */
function MeetingTabs({ meetingId, current }: { meetingId: string; current: MeetingTab }) {
  const laneRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const lane = laneRef.current;
    const currentTab = lane?.querySelector('[aria-current="page"]');
    if (lane && currentTab) revealInLane(lane, currentTab);
  }, [current]);

  return (
    <nav aria-label="This meeting" data-testid="meeting-tabs">
      <ul
        ref={laneRef}
        // Gap 24, then 16 on a phone, then 12 below 360px. The phone tiers are disjoint ranges on
        // purpose: Tailwind emits `max-[360px]:` BEFORE `max-sm:`, so an overlapping `max-sm:gap-16`
        // would win below 360px too.
        className={cn(
          'relative flex gap-24 border-b border-border max-sm:min-[360px]:gap-16 max-[360px]:gap-12',
          TAB_LANE,
        )}
      >
        {TABS.map((entry) => {
          const selected = entry.tab === current;
          return (
            <li key={entry.tab} className="relative shrink-0">
              <Link
                to={entry.to}
                params={{ meetingId }}
                // Exact: the Overview's address is the prefix of the other three, and the router
                // would otherwise mark it current on all of them.
                exact
                aria-current={selected ? 'page' : undefined}
                // The design system's SaaS tab as it renders beside it (Governance's parts): quiet
                // tertiary labels, and the current one in strong text over the brand underline
                // (D-CBG-10). The strong text keys on `aria-current`, as the tab's keys on Base UI's
                // `data-active`, so it holds on hover the same way. Focus is drawn inside, as the
                // tab's is: the lane would cut an outer ring.
                className={cn(
                  'relative block whitespace-nowrap rounded-t-md px-4 py-10 font-sans text-s14 font-w500 tracking-normal text-text-tertiary outline-none transition-colors hover:text-text aria-[current=page]:font-w600 aria-[current=page]:text-text-strong',
                  INSET_FOCUS_RING,
                )}
                data-testid={entry.testId}
              >
                {entry.label}
                {selected ? (
                  <span
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-2 rounded-full bg-brand-600"
                  />
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * A tab's page, on the rhythm of the tabs above it: 20px under the strip and between its cards (the
 * design system's SaaS tabs panel, as the approved mockup lays out the Overview). One element in both
 * of the frame's renders, so a page's content keeps its place in the tree when its meeting arrives.
 */
function TabContent({ underTabs, children }: { underTabs: boolean; children: ReactNode }) {
  return <div className={cn('flex flex-col gap-20', underTabs && '-mt-4')}>{children}</div>;
}

/**
 * Cancelling a meeting — the shipped dialog, unchanged. Exported for the calendar's "Board can't find
 * this meeting" dialog, whose Cancel meeting is this same act (CBG-CAL1 slice 1d).
 */
export function CancelMeetingDialog({
  orgRef,
  meetingId,
  onClose,
  onCancelled,
}: {
  orgRef: string;
  meetingId: string;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const operations = useBoardOperations();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: async () => {
      await operations.officerCancelBoardMeeting({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: { reason: reason.trim() },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded that this meeting was cancelled.');
      onCancelled();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !cancel.isPending && onClose()}>
      <Dialog.Content
        aria-label="Cancel this meeting"
        className="flex flex-col gap-16"
        testId="meeting-cancel-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Cancel this meeting?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          The meeting stays on your list, marked as cancelled, with your reason and the date you
          recorded it. Nothing already recorded about it changes.
        </p>

        {/* The reason is required by the backend, and it is the thing a reader will want years later. */}
        <TextField
          label="Why"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          testId="meeting-cancel-reason"
        />

        {error !== null ? <ErrorMessage testId="meeting-cancel-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={cancel.isPending}
            onClick={onClose}
            testId="meeting-cancel-dismiss"
          >
            Keep meeting
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={cancel.isPending}
            disabled={reason.trim().length === 0 || cancel.isPending}
            onClick={() => {
              setError(null);
              cancel.mutate();
            }}
            testId="meeting-cancel-confirm"
          >
            Cancel meeting
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
